import React, { useEffect, useRef, useState } from 'react';

import CustomLoading from '../../../util_components/custom_loading';
import LoadFailed from '../shared/load_failed';
import TpDialog from '../shared/tp_dialog';
import CreateNotice from './month_run/create_notice';
import RunConfirm from './month_run/run_confirm';
import SelectionBar from './month_run/selection_bar';
import useReadinessList from './use_readiness_list';
import useRunCreate from './month_run/use_run_create';
import { ReadinessPager, TeacherSearch } from './readiness_controls';
import {
  CARRY_FORWARD_TEXT,
  CLOSE_DISABLED_TEXT,
  READINESS_LIST_LOAD_FAILED_TEXT
} from './adapters';
import {
  run_confirm_text,
  selection_changed_text,
  selection_reask_count,
  teacher_months
} from './month_run/adapters';
import { discard_guard_text } from '../shared/dialog_logic';
import { reopen_form_error } from './params';
import {
  RUN_KIND_REOPEN,
  filter_selection,
  list_selection,
  retry_of_run
} from './month_run/params';

/*
 * PROD-7828 R5b + D110 — reopen closed teacher-months (D34, D84) as a server-side run. Teachers are
 * ticked row by row, with "Select all shown", or with "Reopen all N matching" (B7: every CLOSED
 * teacher-month matching the search, fixed when the run starts). Reopening moves money back, so every
 * run is confirmed with its count and month first. Only a teacher's latest closed month can be
 * reopened; the server refuses the others (REOPEN_LATER_MONTH_CLOSED) and the run lists them.
 * seed: {token, rows, reason, run_uuid} pre-picks the teachers of "Retry the teachers that failed"; the new run
 * names run_uuid as the one it retries (the Slack post says so).
 */
const REOPEN_STATE = 'CLOSED';

const by_uuid = (rows) => rows.reduce((acc, r) => ({ ...acc, [r.teacher_uuid]: r }), {});

const ReopenModal = ({
  show,
  pay_month,
  month_label,
  seed = null,
  on_close,
  on_started,
  on_show_run,
  on_error
}) => {
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState({});
  const [match_all, setMatchAll] = useState(null);
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [refusal, setRefusal] = useState(null);
  const [disabled_text, setDisabledText] = useState('');
  const [reason_server_error, setReasonServerError] = useState('');

  const { list, listed_query, search_error, loading, loading_more, failed, load_more, retry } =
    useReadinessList({
      show,
      pay_month,
      state: REOPEN_STATE,
      query,
      on_error,
      error_text: 'Failed to load the closed teacher-months.'
    });
  const { rows } = list;
  const selected = Object.keys(picked);

  const seed_token = seed ? seed.token : 0;
  useEffect(() => {
    if (!show || !seed) return;
    setPicked(by_uuid(seed.rows));
    setReason(seed.reason || '');
    setMatchAll(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, seed_token]);

  useEffect(() => {
    setMatchAll(null);
    setConfirm(null);
  }, [query]);

  // Rows only for a search that was sent and answered (CD-95).
  const list_ready = !loading && !search_error && (failed === '' || failed === 'more');
  // R6H-L4: the list's "Try again" unmounts once pressed; focus waits on the search above the list.
  const search_ref = useRef(null);
  const retry_list = () => {
    if (search_ref.current) search_ref.current.focus({ preventScroll: true });
    retry();
  };

  // CD-63: a pick that a SEARCH hides is dropped, so Reopen never acts on a teacher out of sight.
  useEffect(() => {
    if (!list_ready || !listed_query) return;
    const listed = rows.map((r) => r.teacher_uuid);
    setPicked((p) => {
      const kept = Object.keys(p).filter((u) => listed.indexOf(u) !== -1);
      return kept.length === Object.keys(p).length
        ? p
        : kept.reduce((acc, u) => ({ ...acc, [u]: p[u] }), {});
    });
  }, [rows, listed_query, list_ready]);

  const reset = () => {
    setQuery('');
    setPicked({});
    setMatchAll(null);
    setReason('');
    setTouched(false);
    setConfirm(null);
    setRefusal(null);
    setDisabledText('');
    setReasonServerError('');
  };

  const create = useRunCreate({
    pay_month,
    on_started: (run_uuid, run) => {
      reset();
      on_started(run_uuid, run);
    },
    // A reopen has no D33 pre-check; the server never asks for an override here.
    on_precheck: () => setConfirm(null),
    on_refused: (failure, e) => {
      if (failure.kind === 'selection_changed' && match_all) {
        const again = selection_reask_count(failure);
        if (again) {
          setMatchAll({ ...match_all, count: again });
          setConfirm({ note: selection_changed_text(failure, match_all.count) });
          return;
        }
        // Nothing one run can take: say why, with no confirm to press.
        setMatchAll(null);
        setConfirm(null);
        setRefusal({ message: selection_changed_text(failure, match_all.count), run_uuid: '' });
        return;
      }
      setConfirm(null);
      if (failure.kind === 'disabled') setDisabledText(CLOSE_DISABLED_TEXT);
      else if (failure.kind === 'reason_too_long') {
        setReasonServerError(failure.message || 'The reason is too long.');
      } else if (failure.kind === 'already_active' || failure.kind === 'idempotency_mismatch') {
        setRefusal({ message: failure.message, run_uuid: failure.run_uuid });
      } else on_error(e, 'The reopen was not started. Nothing was changed.');
    }
  });

  const close = () => {
    create.reset();
    reset();
    on_close();
  };

  const count = match_all ? match_all.count : selected.length;
  const form_error = reopen_form_error({ selected_count: count, reason });

  const toggle = (row) =>
    setPicked((p) => {
      if (!p[row.teacher_uuid]) return { ...p, [row.teacher_uuid]: row };
      const { [row.teacher_uuid]: _drop, ...rest } = p;
      return rest;
    });

  const shown = rows.map((r) => r.teacher_uuid);
  const shown_picked = shown.filter((u) => picked[u]).length;
  const toggle_shown = () =>
    setPicked((p) => {
      if (shown.length && shown_picked === shown.length) {
        return Object.keys(p).reduce(
          (acc, u) => (shown.indexOf(u) === -1 ? { ...acc, [u]: p[u] } : acc),
          {}
        );
      }
      return { ...p, ...by_uuid(rows) };
    });

  // The form is checked again on the confirm: a reason cleared meanwhile sends nothing.
  const confirm_send = () => {
    setTouched(true);
    if (form_error || create.busy) {
      setConfirm(null);
      return;
    }
    send();
  };

  const submit = () => {
    setTouched(true);
    if (form_error || create.busy) return;
    setConfirm({ note: '' });
  };

  const send = () => {
    setRefusal(null);
    setReasonServerError('');
    create.send({
      kind: RUN_KIND_REOPEN,
      reason,
      retry_of_run_uuid: retry_of_run(seed, selected, match_all),
      selection: match_all
        ? filter_selection(REOPEN_STATE, match_all.q, match_all.count)
        : list_selection(selected)
    });
  };

  // HL11: the switch is known from the list read, before any reason is typed.
  const switch_text = disabled_text || (list.close_enabled ? '' : CLOSE_DISABLED_TEXT);
  const submit_off = create.busy || !!switch_text;

  return (
    <TpDialog
      show={show}
      on_close={close}
      busy={create.busy}
      // A typed reason (and the teachers picked with it) is not thrown away by a stray Escape/mis-click.
      guard_text={discard_guard_text(reason)}
      title={`Reopen teacher-months · ${month_label}`}
    >
      <div className="tp-modal tp-modal-wide">
        {switch_text ? (
          <div className="tp-banner tp-banner-warn" role="alert">
            {switch_text}
          </div>
        ) : null}
        <div className="tp-ctx">
          Only a teacher&apos;s latest closed month can be reopened: a reopen is refused while any
          later month is closed. To reopen an earlier month, reopen the later closed months first,
          newest first, each with its own reason. Reopening reverses this month&apos;s Free Pass
          grant and re-checks the later months. {CARRY_FORWARD_TEXT} The teachers are reopened one
          by one on the server; you can close this dialog and follow the progress on the page.
        </div>

        <div className="tp-frow">
          <TeacherSearch
            value={query}
            on_change={setQuery}
            error={search_error}
            input_ref={search_ref}
          />
        </div>
        <SelectionBar
          locked={!!confirm}
          shown_count={shown.length}
          shown_picked={shown_picked}
          picked_count={selected.length}
          matching={list_ready && list.has_total ? list.total_count : null}
          match_all={match_all}
          noun="closed teacher-months"
          match_verb="Reopen"
          on_toggle_shown={toggle_shown}
          on_match_all={() => setMatchAll({ q: listed_query, count: list.total_count })}
          on_clear={() => {
            setPicked({});
            setMatchAll(null);
          }}
        />
        {loading ? <CustomLoading /> : null}
        {!loading && failed === 'list' ? (
          <LoadFailed text={READINESS_LIST_LOAD_FAILED_TEXT} on_retry={retry_list} />
        ) : null}
        {list_ready ? (
          <div>
            <div className="tp-na-scroll">
              {rows.map((r) => (
                <div key={r.key} className="tp-na-row">
                  <label className="tp-na-row-main">
                    <input
                      type="checkbox"
                      checked={!!match_all || !!picked[r.teacher_uuid]}
                      onChange={() => (confirm ? undefined : toggle(r))}
                      disabled={!!match_all || !!confirm}
                    />
                    <span>
                      <b>{r.teacher_name}</b>
                      {r.net_text ? ` · ${r.net_text}` : ''}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            {rows.length ? null : (
              <div className="tp-empty">
                {listed_query
                  ? `No closed teacher-months in ${month_label} match "${listed_query}".`
                  : `No closed teacher-months in ${month_label}.`}
              </div>
            )}
            <ReadinessPager
              list={list}
              loading_more={loading_more}
              more_failed={failed === 'more'}
              on_more={load_more}
              on_retry={retry}
            />
          </div>
        ) : null}

        <div className="tp-field">
          <label htmlFor="tp-reopen-reason">Reason (required, recorded in the audit trail)</label>
          <textarea
            id="tp-reopen-reason"
            className="tp-textarea"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setReasonServerError('');
            }}
            onBlur={() => setTouched(true)}
            placeholder="Why is this month being reopened?"
          />
        </div>

        {touched && form_error ? (
          <div className="tp-neg" role="alert">
            {form_error}
          </div>
        ) : null}
        {reason_server_error ? (
          <div className="tp-neg" role="alert">
            {reason_server_error}
          </div>
        ) : null}
        <CreateNotice
          notice={create.notice}
          uncertain={create.uncertain}
          on_retry={create.retry}
          refusal={refusal}
          on_show_run={(run_uuid) => {
            close();
            on_show_run(run_uuid);
          }}
        />

        {confirm ? (
          <RunConfirm
            text={run_confirm_text({ kind: RUN_KIND_REOPEN, count, month_label })}
            note={confirm.note}
            confirm_label={`Reopen ${teacher_months(count)}`}
            busy={create.busy}
            on_confirm={confirm_send}
            on_back={() => setConfirm(null)}
          />
        ) : (
          <div className="tp-modal-ft">
            {/* aria-disabled, not disabled: disabling the focused button drops focus (CD-114). */}
            <button
              type="button"
              className="tp-btn-ghost"
              onClick={create.busy ? undefined : close}
              aria-disabled={create.busy ? 'true' : undefined}
            >
              Cancel
            </button>
            <button
              type="button"
              className="tp-btn tp-btn-danger"
              onClick={submit_off ? undefined : submit}
              aria-disabled={submit_off ? 'true' : undefined}
            >
              {`Reopen ${teacher_months(count)}…`}
            </button>
          </div>
        )}
      </div>
    </TpDialog>
  );
};

export default ReopenModal;
